import { useEffect, useState } from 'react'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import InlineTooltip from 'modules/core/components/InlineTooltip'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import PersonalEmail from 'modules/core/components/Inputs/PersonalEmail'
import DeprecatedText from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/testing/apiClient'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type WorkForm from 'modules/employees/lib/forms/EmployeeForm/WorkInformation'
import { ProfileEmployeeData } from 'modules/profile/components/ProfileForm/types'
import Email from 'modules/profile/components/WorkInformationForm/Email'
import { useCanEditEmail } from 'modules/profile/hooks/personal_info/useCanEditEmail'
import type EmailForm from 'modules/profile/lib/forms/Email'
import type PersonalEmailForm from 'modules/profile/lib/forms/PersonalEmail'

const UserExists = ({ employeeData }: { employeeData: ProfileEmployeeData }) => {
  const companyName = employeeData.company?.name
  const userName = employeeData.access?.fullName
  const interpolations = { companyName, userName }

  const text = employeeData.access?.isConfirmed
    ? i18n.t('settings.users.already_user_in_company', interpolations)
    : i18n.t('settings.users.already_email', interpolations)

  return <InlineTooltip arrow="top">{text}</InlineTooltip>
}

interface EmailProps {
  emailForm: EmailForm
  employeeData: ProfileEmployeeData
  hasFactorialId: boolean
}

const EmailField = ({ employeeData, emailForm, hasFactorialId }: EmailProps) => {
  const accessId = employeeData?.access?.id
  const shouldSkip = !accessId

  const { canEditEmail, loading: loadingCanEdit } = useCanEditEmail(
    { accessId: accessId ?? '0' },
    { skip: shouldSkip }
  )

  const { value: multitenantData, loading: loadingMultitenant } = useAsyncData(
    () => apiClient().get('/accesses/multitenant', { id: accessId }),
    [accessId]
  )

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  const canChangeEmail = canEditEmail
  const isMultitenant = !!(multitenantData as any)?.multitenant
  const loading = loadingCanEdit || loadingMultitenant

  if (!employeeData?._policies?.shareProfileEmail?.can || !accessId) return null

  if (canChangeEmail) {
    return (
      <Email
        form={emailForm}
        canEditEmployeeEmail={canEditEmail}
        employeeData={employeeData}
        isCurrentUser={employeeData.access.id === currentAccessId}
        employeeExists={!!employeeData.access?.id}
        hasFactorialId={hasFactorialId}
      />
    )
  }

  const emailReadonlyTooltip = i18n.t('employee_form.email_readonly_tooltip')

  return (
    <TextField
      readonly
      mandatory
      secondaryLabel={
        !isMultitenant && !loading && <InformationTooltip information={emailReadonlyTooltip} />
      }
      label={i18n.t('employee_form.email')}
      placeholder={i18n.t('add_employee_modal.general_information_step.fields.email.placeholder')}
      value={employeeData.loginEmail ?? ''}
    />
  )
}

interface PersonalEmailProps {
  emailForm: PersonalEmailForm
  employeeData: ProfileEmployeeData
  canSeePersonalInfo: boolean
}

const PersonalEmailField = ({
  employeeData,
  emailForm,
  canSeePersonalInfo,
}: PersonalEmailProps) => {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()

  if (!employeeData) return null

  if (!canSeePersonalInfo) return null

  if (employeeData._policies?.editPersonalInfo?.can) {
    return (
      <PersonalEmail
        form={emailForm}
        employee={employeeData}
        isCurrentUser={employeeData.access.id === currentAccessId}
        employeeExists={!!employeeData.access?.id}
        canEditPersonalEmail={employeeData._policies?.editPersonalInfo?.can}
        placeholder={i18n.t('add_employee_modal.fields.personal_email_placeholder')}
        personalEmail={employeeData.personalEmail ?? ''}
      />
    )
  }

  return (
    <TextField
      readonly
      secondaryLabel={
        <InformationTooltip information={i18n.t('employee_form.personal_email.readonly_tooltip')} />
      }
      label={i18n.t('employee_form.personal_email.label')}
      placeholder={i18n.t('add_employee_modal.fields.personal_email_placeholder')}
      value={employeeData.personalEmail ?? ''}
    />
  )
}

interface Props {
  workForm: WorkForm
  emailForm: EmailForm
  personalEmailForm: PersonalEmailForm
  employeeData: ProfileEmployeeData
  emailExists?: boolean
  canSeePersonalInfo: boolean
  hasFactorialId: boolean
}

const WorkInformationForm = ({
  workForm,
  emailForm,
  personalEmailForm,
  employeeData,
  emailExists,
  canSeePersonalInfo,
  hasFactorialId,
}: Props) => {
  const initialManagerId = employeeData.manager?.id ? String(employeeData.manager.id) : undefined
  const [selectedManagerId, setSelectedManagerId] = useState<string | undefined>(initialManagerId)
  useEffect(() => {
    if (initialManagerId) {
      setSelectedManagerId(initialManagerId)
    }
  }, [initialManagerId])

  const canSeeCompanyIdentifier = employeeData._policies?.readCompanyIdentifier?.can || false
  const canEditCompanyIdentifier = employeeData._policies?.editCompanyIdentifier?.can || false
  const canSeeSeniorityCalculationDate =
    employeeData._policies?.readSeniorityCalculationDate?.can || false
  const canEditSeniorityCalculationDate =
    employeeData._policies?.editSeniorityCalculationDate?.can || false

  return (
    <>
      <FormRow extra={{ 'data-intercom-target': 'employee-form-manager-selector' }}>
        <EmployeeSelectorV2
          label={i18n.t('employee_form.reports_to')}
          disabled={!employeeData._policies?.editManager?.can}
          forcedFilters={{
            companyId: String(employeeData.company?.id),
            excludeIds: [String(employeeData.id)],
          }}
          value={selectedManagerId}
          onChange={(newManagerId) => {
            setSelectedManagerId(newManagerId)
            workForm.setValues({ manager_id: newManagerId })
          }}
        />
      </FormRow>
      <FormRow>
        <EmailField
          employeeData={employeeData}
          emailForm={emailForm}
          hasFactorialId={hasFactorialId}
        />
        {emailExists && <UserExists employeeData={employeeData} />}
      </FormRow>
      <FormRow>
        <PersonalEmailField
          employeeData={employeeData}
          emailForm={personalEmailForm}
          canSeePersonalInfo={canSeePersonalInfo}
        />
      </FormRow>
      <FormRow>
        <TextField
          value={employeeData.legalEntity?.legalName || '-'}
          label={i18n.t('common.legal_entity')}
          readonly
        />
      </FormRow>
      {canSeeCompanyIdentifier && (
        <FormRow>
          <DeprecatedText
            label={i18n.t('employee_form.company_identifier.label')}
            readonly={!canEditCompanyIdentifier}
            field={workForm.get('company_identifier')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('employee_form.company_identifier.information')}
                icon="question"
              />
            }
          />
        </FormRow>
      )}
      {canSeeSeniorityCalculationDate && (
        <FormRow>
          <DayPicker
            readonly={!canEditSeniorityCalculationDate}
            value={
              workForm.values.seniority_calculation_date &&
              dateBackendToUI(workForm.values.seniority_calculation_date)
            }
            onChange={(value) =>
              workForm.setValues({
                seniority_calculation_date: value ? dateUIToBackend(value) : undefined,
              })
            }
            label={i18n.t('employee_form.seniority_calculation_date.label')}
            withNull
            withNullLabel={i18n.t('employee_form.seniority_calculation_date.unknown_date.label')}
            withNullButtonLabel={i18n.t(
              'employee_form.seniority_calculation_date.unknown_date.button'
            )}
          />
        </FormRow>
      )}
    </>
  )
}

export default observer(WorkInformationForm)
